import React, { createContext, useContext, useState, useCallback } from 'react'
import Toast, { type ToastType, type ToastPosition } from './Toast'

interface ToastItem {
  id: string
  message: string
  type?: ToastType
  duration?: number
  position?: ToastPosition
}

interface ToastContextType {
  showToast: (
    message: string,
    type?: ToastType,
    duration?: number,
    position?: ToastPosition
  ) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const showToast = useCallback(
    (
      message: string,
      type: ToastType = 'error',
      duration = 5000,
      position: ToastPosition = 'bottom-right'
    ) => {
      const id = `toast-${Date.now()}-${Math.random()}`
      setToasts((prev) => [...prev, { id, message, type, duration, position }])
    },
    []
  )

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  // Group toasts by position
  const toastsByPosition = toasts.reduce(
    (acc, toast) => {
      const pos = toast.position || 'bottom-right'
      if (!acc[pos]) acc[pos] = []
      acc[pos].push(toast)
      return acc
    },
    {} as Record<ToastPosition, ToastItem[]>
  )

  // Position classes for each corner
  const positionClasses: Record<ToastPosition, string> = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4'
  }

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast Containers for each position */}
      {(Object.keys(positionClasses) as ToastPosition[]).map((position) => {
        const positionToasts = toastsByPosition[position] || []
        if (positionToasts.length === 0) return null

        return (
          <div
            key={position}
            className={`pointer-events-none fixed z-50 flex flex-col gap-2 ${positionClasses[position]}`}
          >
            {positionToasts.map((toast) => (
              <div key={toast.id} className="pointer-events-auto">
                <Toast
                  message={toast.message}
                  type={toast.type}
                  duration={toast.duration}
                  position={toast.position}
                  onClose={() => removeToast(toast.id)}
                />
              </div>
            ))}
          </div>
        )
      })}
    </ToastContext.Provider>
  )
}
